import React, { FC, useEffect, useState } from 'react';
import NextDayBannerStyled from './NextDayBannerStyled';
import { default as CalendarIcon } from '~/components/Icons/outline/Calendar';
import { CloseThin as CloseIcon } from '../Icons/fill';
import InfoCircle from '../Icons/outline/InfoCircle';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import Tooltip from '../Tooltip';
import { setNDIBanner } from '~/redux/actions/products';
import LegalAnnotation from '../LegalAnnotation';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface Props {
  enabled?: string;
  useNextDayInstallLogic?: string;
  content?: string;
  tooltipContent?: string;
  legalKey?: string;
}

const NextDayBanner: FC<Props> = ({ enabled, useNextDayInstallLogic, content, tooltipContent, legalKey }) => {
  const dispatch = useDispatch();
  const [showBanner, setShowBanner] = useState<boolean>(true);
  const [day, setDay] = useState<string>('');
  const [daysAway, setDaysAway] = useState<number>(0);
  const [minutesLeft, setMinutesLeft] = useState<number>(15);
  const { products } = useSelector((store: ReduxState) => store);

  const isTooltipInteractive = tooltipContent?.includes('<a');
  const useNextDayInstallLogicBool = useNextDayInstallLogic === 'true';
  // For some reason I kept getting the Preamp variable name included in the tooltip content, so the below line removes it
  const finalTooltipContent = tooltipContent?.replace('{*nextDayInstallBannerTooltipContent* ', '') || '';
  const legalAnnotation = (legalKey && legalKey !== 'none' && <LegalAnnotation legalKey={legalKey} />) || null;

  useEffect(() => {
    if (!useNextDayInstallLogicBool) return;

    const now = new Date();
    const then = new Date(products?.firstAvailable);

    setDaysAway(Math.ceil((then.getTime() - now.getTime()) / (1000 * 3600 * 24)));
    setDay(DAY_NAMES[then.getDay()]);
  }, [products?.firstAvailable]);

  useEffect(() => {
    if (!useNextDayInstallLogicBool) return;

    if (minutesLeft <= 0) return setShowBanner(false);

    setTimeout(() => {
      setMinutesLeft(minutesLeft - 1);
    }, 60 * 1000);
  }, [minutesLeft]);

  // Keep track of banner being shown in global store so that PS on LP sticky button can adjust styles accordingly
  useEffect(() => {
    dispatch(setNDIBanner(showBanner));
  }, [showBanner]);

  function handleClose() {
    setShowBanner(false);
  }

  if (enabled !== 'true') return null;

  let finalContent = content;

  if (useNextDayInstallLogicBool) {
    if (daysAway >= 7 || !products?.firstAvailable) return null;

    const preMinutesCotent = content.split(':minutesLeft:')[0].trim();
    const postMinutesContent = content.split(':minutesLeft:')[1].split(':day:')[0].trim();
    const postDayContent = content.split(':day:')[1];

    finalContent = `${preMinutesCotent} ${
      minutesLeft > 1 ? `${minutesLeft} minutes` : 'minute'
    } ${postMinutesContent} ${day}${postDayContent}`;
  } else {
    if (!showBanner) return null;
  }

  return (
    <NextDayBannerStyled data-testid="next-day-banner" showBanner={showBanner}>
      <div className="next-day-banner__icon-wrapper" data-testid="next-day-banner-icon">
        <CalendarIcon />
      </div>
      <div className="next-day-banner__copy">
        <span dangerouslySetInnerHTML={{ __html: finalContent }} />
        {legalAnnotation}
        <Tooltip
          tabIndex={0}
          theme="light"
          interactive={isTooltipInteractive}
          html={<span dangerouslySetInnerHTML={{ __html: finalTooltipContent }} />}
        >
          <InfoCircle />
        </Tooltip>
      </div>
      <button className="next-day-banner__close" onClick={handleClose} data-testid="next-day-banner-close">
        <CloseIcon fill="#fff" />
      </button>
    </NextDayBannerStyled>
  );
};

NextDayBanner.displayName = 'NextDayBanner';

export default NextDayBanner;
